<template>
  <section class="notifications-view">
    <h1 class="title">Notificaciones por correo</h1>
    <p>Elige qué correos deseas recibir. Las preferencias se aplican a los eventos habilitados por la administración.</p>
    <p v-if="error" role="alert" class="notification is-danger">{{ error }}</p>
    <p v-if="message" role="status" class="notification is-success">{{ message }}</p>
    <article class="box">
      <h2 class="subtitle">Mis preferencias</h2>
      <label v-for="preference in preferences" :key="preference.event_type" class="preference">
        <input v-model="preference.email_enabled" type="checkbox" :disabled="busy" @change="savePreference(preference)">
        {{ preference.label }}
      </label>
    </article>
    <template v-if="admin">
      <article class="box">
        <h2 class="subtitle">Eventos del sistema</h2>
        <div v-for="rule in rules" :key="rule.event_type" class="rule">
          <label><input v-model="rule.email_enabled" type="checkbox" :disabled="busy"> {{ rule.label }}</label>
          <div v-if="rule.event_type === 'partner_birthdays_weekly'" class="birthday-options">
            <label>Perfiles destinatarios
              <select v-model="rule.profile_ids" multiple class="input profiles" :disabled="busy">
                <option v-for="profile in profiles" :key="profile.id" :value="profile.id">{{ profile.description }}</option>
              </select>
            </label>
            <p>Sin selección se utiliza Usuario Admin. Los perfiles seleccionados reciben permiso para consultar el directorio de cumpleaños de socios.</p>
            <label>Día
              <select v-model.number="rule.weekday" class="input" :disabled="busy">
                <option v-for="(day, index) in days" :key="index" :value="index">{{ day }}</option>
              </select>
            </label>
            <label>Hora <input v-model.number="rule.hour" type="number" min="0" max="23" class="input" :disabled="busy"></label>
            <label>Minuto <input v-model.number="rule.minute" type="number" min="0" max="59" class="input" :disabled="busy"></label>
            <label>Zona horaria <input v-model.trim="rule.timezone" class="input" :disabled="busy"></label>
            <p>El resumen contiene los cumpleaños de lunes a domingo de esa semana.</p>
          </div>
          <button class="button is-primary" :disabled="busy" @click="saveRule(rule)">Guardar {{ rule.label }}</button>
        </div>
      </article>
      <article class="box">
        <h2 class="subtitle">Estado de los envíos</h2>
        <p v-if="status">{{ status.enabled ? 'Procesamiento habilitado' : 'Procesamiento deshabilitado' }} · {{ status.send_enabled ? 'Envío habilitado' : 'Envío pausado' }}</p>
        <p v-if="status && status.workers.length">Última actividad: {{ date(status.workers[0].heartbeat_at) }} <span v-if="status.workers[0].last_error">· Error: {{ status.workers[0].last_error }}</span></p>
        <p v-else>Sin actividad registrada del worker.</p>
        <p v-if="status && status.oldest_pending_at">Pendiente más antiguo: {{ date(status.oldest_pending_at) }}</p>
        <div v-if="status" class="counts"><span v-for="(count, key) in status.counts" :key="key">{{ label(key) }}: {{ count }}</span></div>
        <ul v-if="status"><li v-for="run in status.schedules" :key="run.period">Semana {{ run.period }}: {{ label(run.status) }}</li></ul>
        <button class="button" :disabled="busy" @click="refresh">Actualizar</button>
        <div v-if="status && status.event_failures.length">
          <h3 class="subtitle">Eventos con problemas</h3>
          <div v-for="event in status.event_failures" :key="event.id" class="rule">
            {{ event.event_type }} #{{ event.id }}: {{ label(event.status) }}
            <button v-if="['failed', 'dead_letter'].includes(event.status)" class="button" :disabled="busy" @click="openRetry(event, 'event')">Reintentar evento</button>
          </div>
        </div>
      </article>
      <article class="box">
        <h2 class="subtitle">Historial de correos</h2>
        <p>Aceptado significa que el proveedor recibió el correo. Entregado requiere confirmación del proveedor.</p>
        <label>Estado <select v-model="filter" class="input" @change="page = 1; refresh()"><option value="">Todos</option><option v-for="(text, key) in deliveryLabels" :key="key" :value="key">{{ text }}</option></select></label>
        <div class="table-container">
          <table class="table is-fullwidth">
            <thead><tr><th>Fecha</th><th>Destinatario</th><th>Asunto</th><th>Estado</th><th>Intentos</th><th>Acción</th></tr></thead>
            <tbody><tr v-for="delivery in deliveries" :key="delivery.id">
              <td>{{ date(delivery.created_at) }}</td><td>{{ delivery.recipient_email }}</td><td>{{ delivery.subject }}</td>
              <td>{{ label(delivery.status) }}<small v-if="delivery.error_code">{{ delivery.error_code }}</small></td><td>{{ delivery.attempt_count }}</td>
              <td><button v-if="['failed', 'dead_letter', 'unknown'].includes(delivery.status)" class="button" :disabled="busy" @click="openRetry(delivery, 'delivery')">{{ delivery.status === 'unknown' ? 'Reconciliar' : 'Reintentar' }}</button></td>
            </tr></tbody>
          </table>
        </div>
        <p v-if="!deliveries.length">No hay correos para mostrar.</p>
        <button class="button" :disabled="busy || page === 1" @click="page--; refresh()">Anterior</button>
        <span> Página {{ page }} </span><button class="button" :disabled="busy || page * 50 >= total" @click="page++; refresh()">Siguiente</button>
      </article>
    </template>
    <article v-if="retryTarget" class="box">
      <h2 class="subtitle">{{ retryTarget.status === 'unknown' ? 'Reconciliar envío' : 'Reintentar' }} #{{ retryTarget.id }}</h2>
      <template v-if="retryTarget.status === 'unknown'">
        <label>Resultado confirmado con el proveedor
          <select v-model="outcome" class="input"><option value="not_accepted">No aceptado: reintentar</option><option value="accepted">Aceptado: registrar sin reenviar</option><option value="delivered">Entregado: registrar sin reenviar</option></select>
        </label>
        <label class="preference"><input v-model="reconciled" type="checkbox"> Verifiqué este resultado con el proveedor.</label>
      </template>
      <label>Motivo <textarea v-model.trim="reason" class="textarea" minlength="5" maxlength="500" /></label>
      <button class="button is-primary" :disabled="busy || reason.length < 5 || (retryTarget.status === 'unknown' && !reconciled)" @click="retry">{{ retryTarget.status === 'unknown' && outcome !== 'not_accepted' ? 'Registrar resultado' : 'Registrar y reintentar' }}</button>
      <button class="button" :disabled="busy" @click="retryTarget = null">Cancelar</button>
    </article>
  </section>
</template>

<script>
import auth from '@/auth/index'

const deliveryLabels = { pending: 'Pendiente', sending: 'Enviando', accepted: 'Aceptado', sent: 'Aceptado (histórico)', delivered: 'Entregado', failed: 'Reintento pendiente', dead_letter: 'Fallo definitivo', unknown: 'Resultado incierto', cancelled: 'Cancelado', suppressed: 'Excluido', simulated: 'Simulado' }

export default {
  data () {
    return { preferences: [], rules: [], profiles: [], status: null, deliveries: [], page: 1, total: 0, filter: '', busy: false, error: '', message: '', retryTarget: null, retryKind: '', reason: '', reconciled: false, outcome: 'not_accepted', deliveryLabels, days: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'] }
  },
  computed: { admin () { return auth.canManageUsers() } },
  mounted () { this.load() },
  methods: {
    label (status) { return deliveryLabels[status] || { expired: 'Vencido', empty: 'Sin cumpleaños', enqueued: 'Programado' }[status] || status },
    date (value) { return value ? new Date(value).toLocaleString('es-CL') : '—' },
    async action (work, message) {
      this.busy = true
      this.error = this.message = ''
      try { await work(); this.message = message || '' } catch (error) { this.error = (error.response && error.response.data && error.response.data.message) || 'No fue posible completar la operación.' } finally { this.busy = false }
    },
    async load () {
      await this.action(async () => {
        this.preferences = (await this.$axios.get('notifications/preferences')).data.preferences
        if (this.admin) {
          const data = (await this.$axios.get('notifications/rules')).data
          this.rules = data.rules
          this.profiles = data.profiles
          await this.loadHistory()
        }
      })
    },
    async loadHistory () {
      this.status = (await this.$axios.get('notifications/status')).data
      const data = (await this.$axios.get('notifications/history', { params: { page: this.page, status: this.filter } })).data
      this.deliveries = data.deliveries
      this.total = data.total
    },
    refresh () { return this.action(() => this.loadHistory()) },
    async savePreference (preference) {
      await this.action(async () => {
        try { await this.$axios.post('notifications/preferences', preference) } catch (error) { preference.email_enabled = !preference.email_enabled; throw error }
      }, 'Preferencia guardada.')
    },
    saveRule (rule) { return this.action(() => this.$axios.post('notifications/rules', rule), 'Configuración guardada.') },
    openRetry (target, kind) { this.retryTarget = target; this.retryKind = kind; this.reason = ''; this.reconciled = false; this.outcome = 'not_accepted' },
    retry () {
      return this.action(async () => {
        const path = this.retryKind === 'event' ? 'events' : 'deliveries'
        const resolving = this.retryTarget.status === 'unknown' && this.outcome !== 'not_accepted'
        await this.$axios.post(`notifications/${path}/${this.retryTarget.id}/${resolving ? 'resolve' : 'retry'}`, { reason: this.reason, reconciliation: this.reconciled ? 'not_accepted' : null, outcome: this.outcome })
        this.retryTarget = null
        await this.loadHistory()
      }, 'Operación registrada.')
    }
  }
}
</script>

<style scoped>
.notifications-view { max-width: 1100px; margin: auto; padding: 1.5rem; }
.box { margin-top: 1.5rem; }
.preference, .birthday-options label { display: block; margin: .75rem 0; }
.rule { border-top: 1px solid #ddd; padding: 1rem 0; }
.rule button { margin-top: .75rem; }
.birthday-options { max-width: 600px; }
.profiles { height: 9rem; }
.counts { display: flex; gap: 1rem; flex-wrap: wrap; margin: 1rem 0; }
small { display: block; }
</style>
